/**
 * Subscription plans as the dashboard shows them (W9). Everything a plan screen says comes
 * from two tables: `subscription_plans` (one row per plan) and `subscription_plan_sections`
 * (page copy that belongs to no single plan: intro, "How commission works", "On every plan").
 * Prices, rates and copy live there, never in code. The copy is the user's pricing text of
 * 2026-10-24, stored verbatim — do not reword it here.
 *
 * Commission and setup fees are DISPLAY ONLY: nothing measures or charges them yet.
 * Legacy plan ids (standard/premium/enterprise, provider_small/large) are hidden rows in
 * the same table, so a subscriber still on one sees its real name in the plan badge.
 */

/** The sync line used outside the plan cards (app Landing). Same wording as the pricing copy. */
export const SYNC_PROMISE = 'Stock and prices updated in real time';

export const PLAN_COLUMNS =
  'id, audience, name, tagline, description, price_amount, price_label, currency, billing_interval, ' +
  'commission_pct, commission_label, reseller_commission_pct, setup_fee_amount, setup_fee_label, ' +
  'example_sales_amount, features, cta_label, contact_url, is_public, highlight, sort_order';

export const SECTION_COLUMNS = 'id, audience, kind, heading, subheading, body, items, link_label, link_url, sort_order';

export type PlanAudience = 'merchant' | 'provider';

type Numeric = number | string | null;

export type Plan = {
  id: string;
  audience: PlanAudience;
  name: string;
  tagline?: string | null;
  description?: string | null;
  /** null = quote-only, no price shown */
  price_amount: Numeric;
  price_label?: string | null;
  currency: string;
  billing_interval: 'month' | 'year';
  commission_pct?: Numeric;
  commission_label?: string | null;
  reseller_commission_pct?: Numeric;
  setup_fee_amount?: Numeric;
  setup_fee_label?: string | null;
  example_sales_amount?: Numeric;
  features: string[] | null;
  /** set = sold via a call: the button links to contact_url, there is no checkout */
  cta_label?: string | null;
  contact_url: string | null;
  is_public: boolean;
  highlight: boolean;
  sort_order: number;
};

export type PlanSection = {
  id: string;
  audience: PlanAudience;
  kind: 'intro' | 'list' | 'cta';
  heading: string;
  subheading: string | null;
  body: string | null;
  items: string[] | null;
  link_label: string | null;
  link_url: string | null;
  sort_order: number;
};

const num = (v: Numeric | undefined): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

/** The plans offered on a plan screen, in display order. */
export const publicPlans = (plans: Plan[], audience: PlanAudience): Plan[] =>
  plans
    .filter((p) => p.is_public && p.audience === audience)
    .sort((a, b) => a.sort_order - b.sort_order);

export const sectionsFor = (sections: PlanSection[], audience: PlanAudience, kind?: PlanSection['kind']): PlanSection[] =>
  sections
    .filter((s) => s.audience === audience && (!kind || s.kind === kind))
    .sort((a, b) => a.sort_order - b.sort_order);

export const isQuoteOnly = (plan: Plan): boolean => plan.price_amount === null;

/** Sold via a call ("Book a demo") rather than self-serve checkout. */
export const isSalesLed = (plan: Plan): boolean => Boolean(plan.cta_label);

/** "€49", "€1,500", "€49.50". */
export const formatMoney = (amount: number, currency = 'EUR'): string =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: currency || 'EUR',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);

/** Price of the plan, or null for a quote-only plan. */
export const formatPlanPrice = (plan: Plan): string | null => {
  const amount = num(plan.price_amount);
  return amount === null ? null : formatMoney(amount, plan.currency);
};

/** Words after the price: the row's own label ("fixed per month"), else "/month". */
export const priceSuffix = (plan: Plan): string =>
  plan.price_label || (plan.billing_interval === 'year' ? '/year' : '/month');

/** "2%", "0.5%", "0.7%". */
export const formatPct = (pct: number): string => `${Number(pct.toFixed(2))}%`;

/** ["2%", "commission"], or null when the plan has no commission. */
export const commissionParts = (plan: Plan): [string, string] | null => {
  const pct = num(plan.commission_pct);
  return pct === null ? null : [formatPct(pct), plan.commission_label || 'commission'];
};

/** "€249" or "On request", or null when the plan has no setup fee line. */
export const setupFeeText = (plan: Plan): string | null => {
  const amount = num(plan.setup_fee_amount);
  if (amount !== null) return formatMoney(amount, plan.currency);
  return plan.setup_fee_label || null;
};

/** What the example sales volume costs in a month: fixed fee + commission on it. */
export const exampleCost = (plan: Plan): number | null => {
  const sales = num(plan.example_sales_amount);
  const price = num(plan.price_amount);
  const pct = num(plan.commission_pct);
  if (sales === null || price === null || pct === null) return null;
  return Math.round((price + (sales * pct) / 100) * 100) / 100;
};

/** ["€10,000", "€249"] for "Example: €10,000 in sales through FLOWSTOCK per month costs €249." */
export const exampleParts = (plan: Plan): [string, string] | null => {
  const cost = exampleCost(plan);
  const sales = num(plan.example_sales_amount);
  if (cost === null || sales === null) return null;
  return [formatMoney(sales, plan.currency), formatMoney(cost, plan.currency)];
};

/** Name for a subscription's plan_id; falls back to the raw id if the row is unknown. */
export const planName = (plans: Plan[], planId: string | null | undefined): string | undefined => {
  if (!planId) return undefined;
  return plans.find((p) => p.id === planId)?.name ?? planId;
};

// --- Onboarding: trial on the selected plan, setup fee paid upfront (W9b) ---------------------

/** The store plan named by ?plan= in an invite link, if it is one a new store can pick. */
export const planFromQuery = (plans: Plan[], value: string | null): string | null => {
  const id = (value ?? '').trim().toLowerCase();
  return publicPlans(plans, 'merchant').some((p) => p.id === id) ? id : null;
};

/** A plan whose trial only starts once the one-off setup fee is paid (an amount, or "On request"). */
export const hasSetupFee = (plan: Plan): boolean =>
  (num(plan.setup_fee_amount) ?? 0) > 0 || Boolean(plan.setup_fee_label);

/**
 * The plan's Stripe Payment Link, tied to this store. Stripe copies client_reference_id onto the
 * payment, which is how the webhook knows whose trial to start; null when there is no valid link.
 */
export const setupFeeLink = (url: string | null | undefined, merchantId: string, email?: string): string | null => {
  if (!url) return null;
  try {
    const link = new URL(url);
    if (link.protocol !== 'https:') return null;
    link.searchParams.set('client_reference_id', merchantId);
    if (email) link.searchParams.set('prefilled_email', email);
    return link.toString();
  } catch {
    return null;
  }
};

/** A store created on a plan with a setup fee waits here until the fee is paid or waived. */
export const awaitingSetupFee = (sub: { status?: string | null } | null | undefined): boolean =>
  sub?.status === 'pending_setup';
